import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { SecurityProvider } from "@/components/SecurityProvider";
import { GamificationProvider } from "@/contexts/GamificationContext";
import { WeeklyChallengesProvider } from "@/contexts/WeeklyChallengesContext";
import { ThemeProvider } from "next-themes";
import { BottomNavigation } from "@/components/BottomNavigation";
import { InstallPrompt } from "@/components/InstallPrompt";
import { OfflineIndicator, OnlineToast } from "@/components/OfflineIndicator";
import { WhatsAppButton } from "@/components/WhatsAppButton";
import { AnimatePresence, motion } from "framer-motion";
import { lazy, Suspense } from "react";
import Index from "./pages/Index";

// Lazy load all routes except Index (landing page)
const Auth = lazy(() => import("./pages/Auth"));
const Onboarding = lazy(() => import("./pages/Onboarding"));
const Dashboard = lazy(() => import("./pages/Dashboard"));
const Nutrition = lazy(() => import("./pages/Nutrition"));
const Progress = lazy(() => import("./pages/Progress"));
const Macros = lazy(() => import("./pages/Macros"));
const Workouts = lazy(() => import("./pages/Workouts"));
const Analytics = lazy(() => import("./pages/Analytics"));
const Ranking = lazy(() => import("./pages/Ranking"));
const Social = lazy(() => import("./pages/Social"));
const Report = lazy(() => import("./pages/Report"));
const Help = lazy(() => import("./pages/Help"));
const Pricing = lazy(() => import("./pages/Pricing"));
const Timer = lazy(() => import("./pages/Timer"));
const Terms = lazy(() => import("./pages/Terms"));
const Privacy = lazy(() => import("./pages/Privacy"));
const PublicProfile = lazy(() => import("./pages/PublicProfile"));
const NotFound = lazy(() => import("./pages/NotFound"));

const queryClient = new QueryClient();

// Global safety net for unhandled promise rejections (e.g. browser extensions like MetaMask)
if (typeof window !== 'undefined') {
  window.addEventListener('unhandledrejection', (event) => {
    const msg = String(event.reason?.message || event.reason || '');
    if (msg.includes('MetaMask') || msg.includes('ethereum')) {
      event.preventDefault();
      console.warn('Suppressed browser extension error:', msg);
    }
  });
}

// Routes that should show bottom navigation
const BOTTOM_NAV_ROUTES = [
  "/dashboard",
  "/nutrition", 
  "/workouts",
  "/progress",
  "/ranking",
  "/macros",
  "/analytics",
  "/social",
  "/report",
  "/help",
  "/timer"
];

// Page transition variants
const pageVariants = {
  initial: {
    opacity: 0,
  },
  enter: {
    opacity: 1,
    transition: {
      duration: 0.2,
      ease: "easeOut" as const,
    },
  },
  exit: {
    opacity: 0,
    transition: {
      duration: 0.1,
      ease: "easeIn" as const,
    },
  },
};

const AnimatedRoutes = () => {
  const location = useLocation();

  return (
    <AnimatePresence mode="popLayout" initial={false}>
      <motion.div
        key={location.pathname}
        initial="initial"
        animate="enter"
        exit="exit"
        variants={pageVariants}
        className="min-h-screen"
      >
        <Suspense fallback={<div className="min-h-screen" />}>
          <Routes location={location}>
            <Route path="/" element={<Index />} />
            <Route path="/auth" element={<Auth />} />
            <Route path="/onboarding" element={<Onboarding />} />
            <Route path="/dashboard" element={<Dashboard />} />
            <Route path="/nutrition" element={<Nutrition />} />
            <Route path="/workouts" element={<Workouts />} />
            <Route path="/progress" element={<Progress />} />
            <Route path="/macros" element={<Macros />} />
            <Route path="/analytics" element={<Analytics />} />
            <Route path="/ranking" element={<Ranking />} />
            <Route path="/social" element={<Social />} />
            <Route path="/report" element={<Report />} />
            <Route path="/help" element={<Help />} />
            <Route path="/pricing" element={<Pricing />} />
            <Route path="/timer" element={<Timer />} />
            <Route path="/terms" element={<Terms />} />
            <Route path="/privacy" element={<Privacy />} />
            <Route path="/profile/:friendCode" element={<PublicProfile />} />
            {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
            <Route path="*" element={<NotFound />} />
          </Routes>
        </Suspense>
      </motion.div>
    </AnimatePresence>
  );
};

const AppContent = () => {
  const location = useLocation();
  const showBottomNav = BOTTOM_NAV_ROUTES.some(route => 
    location.pathname === route || location.pathname.startsWith(route + "/")
  );

  return (
    <>
      <AnimatedRoutes />
      {showBottomNav && <BottomNavigation />}
      <InstallPrompt />
      <OfflineIndicator />
      <OnlineToast />
      <WhatsAppButton />
    </>
  );
};

const App = () => (
  <QueryClientProvider client={queryClient}>
    <ThemeProvider attribute="class" defaultTheme="system" enableSystem>
      <SecurityProvider>
        <GamificationProvider>
          <WeeklyChallengesProvider>
            <TooltipProvider>
              <Toaster />
              <Sonner />
              <BrowserRouter>
                <AppContent />
              </BrowserRouter>
            </TooltipProvider>
          </WeeklyChallengesProvider>
        </GamificationProvider>
      </SecurityProvider>
    </ThemeProvider>
  </QueryClientProvider>
);

export default App;
